import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  viewChild,
} from '@angular/core';
import { interval, of, Subject } from 'rxjs';
import { startWith, map, tap } from 'rxjs/operators';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color: 'green' | 'red';
  locationName?: string;
}

interface DisplayCalendarEvent extends CalendarEvent {
  height: string;
  top: string;
  width: string;
  left: string;
  layer?: number;
  lengthMinutes: number;
  showDate?: boolean;
  showLocation?: boolean;
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  imports: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class CalendarComponent implements AfterViewInit {
  // Inputs
  readonly events = input.required<CalendarEvent[]>();
  readonly locale = input.required<string>();

  private readonly scroll = viewChild<ElementRef<HTMLElement>>('scrollView');

  // Current time chip position
  private readonly time$ = interval(1000).pipe(
    startWith(0),
    map(() => new Date()),
    takeUntilDestroyed()
  );
  protected readonly now = toSignal<Date>(this.time$);
  protected readonly currentTop = computed(() => {
    const now = this.now();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return this.msToRem(now.getTime() - startOfDay.getTime());
  });

  // Hours in the day
  protected readonly hours = computed(() => {
    return Array.from({ length: 25 }, (_, i) => i).map((hour) => {
      const date = new Date();
      date.setHours(hour, 0, 0, 0);
      return date;
    });
  });

  // Events rendered (based on input events)
  protected readonly displayEvents = computed<DisplayCalendarEvent[]>(() => {
    const events = this.events();
    return this.mapEventsToDisplay(events);
  });

  ngAfterViewInit() {
    setTimeout(() => {
      const el = this.scroll();
      this.scrollToCurrentTime(el);
    }, 1);
  }

  private scrollToCurrentTime(el: ElementRef<HTMLElement>) {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const topPos = this.msToRem(now.getTime() - startOfDay.getTime()).replace(
      'rem',
      ''
    );
    const scrollToPos = parseFloat(topPos) * 16 * 0.8;

    if (!el || !el.nativeElement) return;
    el.nativeElement.scrollTo({
      top: scrollToPos,
    });
  }

  private mapEventsToDisplay(events: CalendarEvent[]): DisplayCalendarEvent[] {
    // Calc the base height and top position
    const baseEvents = events.map((event) => {
      const midnight = new Date().setHours(0, 0, 0, 0);
      const length =
        event.end.getDate() === new Date().getDate()
          ? event.end.getTime() - event.start.getTime()
          : new Date().setHours(24, 0, 0, 0) - event.start.getTime();
      const top = this.msToRem(event.start.getTime() - midnight);
      const height = this.msToRem(length);
      return {
        ...event,
        height,
        top,
        width: '',
        left: '',
        layer: 0,
        lengthMinutes: length / (60 * 1000),
        showDate: length >= 25 * 60 * 1000, // 25 min
        showLocation: length >= 35 * 60 * 1000, // 35 min
      };
    });

    // Sort by start time and duration
    baseEvents.sort((a, b) => {
      const timeDiff = a.start.getTime() - b.start.getTime();
      if (timeDiff !== 0) return timeDiff;
      return b.lengthMinutes - a.lengthMinutes;
    });

    const result: DisplayCalendarEvent[] = [];
    const eventsWithColumns: DisplayCalendarEvent[][] = [[]];

    // Calc in which column each event goes
    for (const event of baseEvents) {
      let addedToColumn = false;
      for (const column of eventsWithColumns) {
        const overlap = column.some((e) =>
          this.doEventsOverlap(event, {
            ...e,
            end: new Date(e.start.getTime() + 35 * 60 * 1000), // 35 min overlap to prevent text overlap
          })
        );
        if (!overlap) {
          column.push(event);
          addedToColumn = true;
          break;
        }
      }
      if (!addedToColumn) {
        eventsWithColumns.push([event]); // new column
      }
    }

    // Calc width and left (layer)
    for (let colIndex = 0; colIndex < eventsWithColumns.length; colIndex++) {
      const column = eventsWithColumns[colIndex];
      const numColumns = eventsWithColumns.length;
      const columnWidth = 100 / numColumns;

      for (let i = 0; i < column.length; i++) {
        const event = column[i];
        let columnsToSpan = 1;
        for (let rightCol = colIndex + 1; rightCol < numColumns; rightCol++) {
          const hasOverlap = eventsWithColumns[rightCol].some((e) =>
            this.doEventsOverlap(e, {
              ...event,
              end: new Date(event.start.getTime() + 35 * 60 * 1000),
            })
          );
          if (hasOverlap) {
            break;
          }
          columnsToSpan++;
        }

        let maxLayer = 0;
        for (let j = 0; j < i; j++) {
          const prevEvent = column[j];
          if (this.doEventsOverlap(prevEvent, event)) {
            maxLayer = Math.max(maxLayer, (prevEvent.layer || 0) + 1);
          }
        }
        event.layer = maxLayer;

        // prettier-ignore
        const width = `calc(${columnWidth * columnsToSpan}% - ${event.layer * 0.3}rem)`;
        // prettier-ignore
        const left = `calc(${columnWidth * colIndex}% + ${event.layer * 0.3}rem)`;

        result.push({
          ...event,
          width,
          left,
        });
      }
    }

    return result;
  }

  private doEventsOverlap(
    event1: CalendarEvent,
    event2: CalendarEvent
  ): boolean {
    const start1 = event1.start;
    const end1 = event1.end;
    const start2 = event2.start;
    const end2 = event2.end;

    return start1 < end2 && end1 > start2;
  }

  private msToRem(timeMs: number): string {
    return timeMs / (10 * 60 * 1000) + 'rem';
  }
}
